// =============================================================
// VARIANCE — The Executive Command Center
// File      : HR.tsx
// Location  : /home/Applications/variance/web/src/pages/HR.tsx
// =============================================================

import { useState, useEffect } from 'react';
import { Users, Plus, X, HelpCircle, Briefcase, ChevronRight, UserPlus, FileText, CheckCircle, Filter } from 'lucide-react';
import { PageHelp, HelpTooltip } from '../components/HelpSystem';

interface Staff {
  id: string; employee_code: string; full_name: string; email: string; phone: string;
  designation: string; department_name: string; reports_to_name: string;
  date_of_joining: string; is_active: boolean; photo_url: string | null;
  home_department_id: string; designation_id: string; assigned_project_ids: string[];
}
interface Department { id: string; name: string; }
interface Designation { id: string; name: string; }
interface Assignment { id: string; role: string; allocation_pct: number; staff_name: string; project_name: string; }

export function HR() {
  const [activeTab, setActiveTab] = useState<'directory' | 'departments' | 'assignments'>('directory');
  const [staff, setStaff] = useState<Staff[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modals & Slide-outs
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false); 
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);

  // Form States
  const [staffData, setStaffData] = useState({ 
  full_name: '', email: '', phone: '', designation_id: '', department_id: '', 
  reports_to_staff_id: '', date_of_joining: '', photo_url: '', company_id: '',
  date_of_birth: '', age: '', contact_address: '', placement_location: '' });

  const [dobType, setDobType] = useState<'dob' | 'age'>('dob');

  const [newDeptName, setNewDeptName] = useState('');
  const [assignmentData, setAssignmentData] = useState({ staff_id: '', project_id: '', role: '', allocation_pct: '100' });
  
  // Inline Creation States
  const [isAddingInlineDept, setIsAddingInlineDept] = useState(false);
  const [isAddingInlineDesig, setIsAddingInlineDesig] = useState(false);
  const [newDesigName, setNewDesigName] = useState('');
  
  // Global Data Lists
  const [companies, setCompanies] = useState<{id: string, name: string}[]>([]);
  const [projects, setProjects] = useState<{id: string, name: string}[]>([]);
  
  // Directory Filters
  const [filterCompany, setFilterCompany] = useState('All');
  const [filterProject, setFilterProject] = useState('All');
  const [filterDept, setFilterDept] = useState('All');
  const [filterDesig, setFilterDesig] = useState('All');

  // Profile Slide-out Tabs
  const [activeProfileTab, setActiveProfileTab] = useState<'profile' | 'salary' | 'kpi'>('profile');
  const [salaryData, setSalaryData] = useState<any>(null);
  const [revisions, setRevisions] = useState<any[]>([]);
  const [kpiData, setKpiData] = useState<any[]>([]);
  const [isSalaryLoading, setIsSalaryLoading] = useState(false);

  // Salary & Appraisal Form States
  const [isSalaryFormOpen, setIsSalaryFormOpen] = useState(false);
  const [isRevisionFormOpen, setIsRevisionFormOpen] = useState(false);
  const [isKpiFormOpen, setIsKpiFormOpen] = useState(false);

  const [salaryForm, setSalaryForm] = useState({ basic_salary: '', hra: '', special_allowance: '', pf_deduction: '', tax_deduction: '' });
  const [revisionForm, setRevisionForm] = useState({ revision_amount: '', new_net_salary: '', reason: '', effective_date: new Date().toISOString().split('T')[0] });
  const [kpiForm, setKpiForm] = useState({ review_period: '', kpi_score: '', reviewer_comments: '', appraisal_date: new Date().toISOString().split('T')[0] });

  const fetchData = () => {
    setIsLoading(true);
    Promise.all([
      fetch('/api/hr/staff').then(res => res.json()),
      fetch('/api/hr/departments').then(res => res.json()),
      fetch('/api/hr/assignments').then(res => res.json()),
      fetch('/api/hr/designations').then(res => res.json()),
      fetch('/api/projects').then(res => res.json()),
      fetch('/api/companies').then(res => res.json())
    ]).then(([staffData, deptData, assignData, desigData, projData, compData]) => {
      if (Array.isArray(staffData)) setStaff(staffData);
      if (Array.isArray(deptData)) setDepartments(deptData);
      if (Array.isArray(assignData)) setAssignments(assignData);
      if (Array.isArray(desigData)) setDesignations(desigData);
      if (Array.isArray(projData)) setProjects(projData);
      if (Array.isArray(compData)) setCompanies(compData);
      setIsLoading(false);
    }).catch(() => setIsLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleStaffSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch('/api/hr/staff', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(staffData)
    });
    setIsStaffModalOpen(false);
    setStaffData({ full_name: '', email: '', phone: '', designation_id: '', department_id: '', reports_to_staff_id: '', date_of_joining: '', photo_url: '' });
    fetchData();
  };

  const handleDeptSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDeptName) return;
    
    await fetch('/api/hr/departments', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        name: newDeptName,
        company_id: companies.length > 0 ? companies[0].id : null 
      })
    });
    
    setNewDeptName('');
    setIsDeptModalOpen(false);
    fetchData();
  };

  const handleAssignmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch('/api/hr/assignments', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...assignmentData, allocation_pct: parseFloat(assignmentData.allocation_pct) })
    });
    setIsAssignmentModalOpen(false);
    setAssignmentData({ staff_id: '', project_id: '', role: '', allocation_pct: '100' });
    fetchData();
  };

  const openStaffProfile = async (staffMember: Staff) => {
    setSelectedStaff(staffMember);
    setActiveProfileTab('profile');
    setIsSalaryLoading(true);
    Promise.all([
      fetch(`/api/hr/staff/${staffMember.id}/salary`).then(res => res.json()),
      fetch(`/api/hr/staff/${staffMember.id}/revisions`).then(res => res.json()),
      fetch(`/api/hr/staff/${staffMember.id}/kpi`).then(res => res.json())
    ]).then(([sal, rev, kpi]) => {
      setSalaryData(sal);
      setRevisions(Array.isArray(rev) ? rev : []);
      setKpiData(Array.isArray(kpi) ? kpi : []);
      setIsSalaryLoading(false);
    }).catch(() => setIsSalaryLoading(false));
  };

  const handleSalarySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch(`/api/hr/staff/${selectedStaff?.id}/salary`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(salaryForm)
    });
    setIsSalaryFormOpen(false); if (selectedStaff) openStaffProfile(selectedStaff);
  };

  const handleRevisionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch(`/api/hr/staff/${selectedStaff?.id}/revisions`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(revisionForm)
    });
    setIsRevisionFormOpen(false); if (selectedStaff) openStaffProfile(selectedStaff);
  };

  const handleKpiSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch(`/api/hr/staff/${selectedStaff?.id}/kpi`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(kpiForm)
    });
    setIsKpiFormOpen(false); if (selectedStaff) openStaffProfile(selectedStaff);
  };

  const filteredStaff = staff.filter(s => {
    if (filterDept !== 'All' && s.home_department_id !== filterDept) return false;
    if (filterDesig !== 'All' && s.designation_id !== filterDesig) return false;
    if (filterProject !== 'All' && (!s.assigned_project_ids || !s.assigned_project_ids.includes(filterProject))) return false;
    if (filterCompany !== 'All') {
      const companyProjectIds = projects.filter(p => (p as any).company_id === filterCompany).map(p => p.id);
      const hasCompanyProject = s.assigned_project_ids?.some(pid => companyProjectIds.includes(pid));
      if (!hasCompanyProject) return false;
    }
    return true;
  });

  if (isLoading) return <div className="p-10 text-center text-white">Loading HR Data...</div>;

  return (
    <div className="space-y-6 max-w-[1600px] relative min-h-screen pt-4">
      
      <PageHelp 
        title="Human Resources & Matrix Allocation" 
        description="Manage your permanent HR hierarchy and temporary project assignments independently."
        action={
          <button onClick={() => setIsHelpOpen(true)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold bg-[var(--brand-accent)] text-white hover:opacity-90">
            <HelpCircle size={14} /> HR Architecture Help
          </button>
        }
      />

      {/* ========================================================= */}
      {/* HR FORMS & MODALS (MUST BE INSIDE THE COMPONENT)          */}
      {/* ========================================================= */}

      {/* ADD/EDIT SALARY MODAL */}
      {isSalaryFormOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1 absolute inset-0" onClick={() => setIsSalaryFormOpen(false)}></div>
          <div className="w-full max-w-md bg-[var(--bg-panel)] shadow-2xl border border-[var(--border-subtle)] rounded-xl flex flex-col relative z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)] rounded-t-xl">
              <h2 className="text-xl font-bold text-white">Edit Salary Structure</h2>
              <button onClick={() => setIsSalaryFormOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleSalarySubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs text-gray-400">Basic Salary</label><input required type="number" value={salaryForm.basic_salary} onChange={e => setSalaryForm({...salaryForm, basic_salary: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" /></div>
                <div><label className="text-xs text-gray-400">HRA</label><input required type="number" value={salaryForm.hra} onChange={e => setSalaryForm({...salaryForm, hra: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" /></div>
                <div><label className="text-xs text-gray-400">Special Allowance</label><input required type="number" value={salaryForm.special_allowance} onChange={e => setSalaryForm({...salaryForm, special_allowance: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" /></div>
                <div><label className="text-xs text-gray-400">PF Deduction</label><input required type="number" value={salaryForm.pf_deduction} onChange={e => setSalaryForm({...salaryForm, pf_deduction: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" /></div>
                <div><label className="text-xs text-gray-400">Tax Deduction</label><input required type="number" value={salaryForm.tax_deduction} onChange={e => setSalaryForm({...salaryForm, tax_deduction: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" /></div>
              </div>
              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Salary</button>
            </form>
          </div>
        </div>
      )}

      {/* ADD REVISION MODAL */}
      {isRevisionFormOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1 absolute inset-0" onClick={() => setIsRevisionFormOpen(false)}></div>
          <div className="w-full max-w-md bg-[var(--bg-panel)] shadow-2xl border border-[var(--border-subtle)] rounded-xl flex flex-col relative z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)] rounded-t-xl">
              <h2 className="text-xl font-bold text-white">Add Salary Revision</h2>
              <button onClick={() => setIsRevisionFormOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleRevisionSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div><label className="text-xs text-gray-400">Revision Amount (₹)</label><input required type="number" value={revisionForm.revision_amount} onChange={e => setRevisionForm({...revisionForm, revision_amount: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" /></div>
              <div><label className="text-xs text-gray-400">New Net Salary (₹)</label><input required type="number" value={revisionForm.new_net_salary} onChange={e => setRevisionForm({...revisionForm, new_net_salary: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" /></div>
              <div><label className="text-xs text-gray-400">Reason</label><input required type="text" value={revisionForm.reason} onChange={e => setRevisionForm({...revisionForm, reason: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" /></div>
              <div><label className="text-xs text-gray-400">Effective Date</label><input required type="date" value={revisionForm.effective_date} onChange={e => setRevisionForm({...revisionForm, effective_date: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" style={{ colorScheme: 'dark' }} /></div>
              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Apply Revision</button>
            </form>
          </div>
        </div>
      )}

      {/* ADD KPI MODAL */}
      {isKpiFormOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1 absolute inset-0" onClick={() => setIsKpiFormOpen(false)}></div>
          <div className="w-full max-w-md bg-[var(--bg-panel)] shadow-2xl border border-[var(--border-subtle)] rounded-xl flex flex-col relative z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)] rounded-t-xl">
              <h2 className="text-xl font-bold text-white">Add KPI Appraisal</h2>
              <button onClick={() => setIsKpiFormOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleKpiSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div><label className="text-xs text-gray-400">Review Period</label><input required type="text" value={kpiForm.review_period} onChange={e => setKpiForm({...kpiForm, review_period: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" placeholder="e.g. Q1 2026" /></div>
              <div><label className="text-xs text-gray-400">KPI Score (out of 100)</label><input required type="number" min="0" max="100" value={kpiForm.kpi_score} onChange={e => setKpiForm({...kpiForm, kpi_score: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" /></div>
              <div><label className="text-xs text-gray-400">Reviewer Comments</label><textarea rows={3} value={kpiForm.reviewer_comments} onChange={e => setKpiForm({...kpiForm, reviewer_comments: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" /></div>
              <div><label className="text-xs text-gray-400">Appraisal Date</label><input required type="date" value={kpiForm.appraisal_date} onChange={e => setKpiForm({...kpiForm, appraisal_date: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" style={{ colorScheme: 'dark' }} /></div>
              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Appraisal</button>
            </form>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white">Human Resources</h1>
          <p className="text-sm text-gray-400 mt-1">Directory, Departments, Designations, and Matrix Assignments</p>
        </div>
        <div className="flex gap-3">
          {activeTab === 'directory' && (
            <button onClick={() => setIsStaffModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white shadow-[0_0_15px_var(--brand-accent)]" style={{ backgroundColor: 'var(--brand-accent)' }}>
              <Plus size={18} /> Add Employee
            </button>
          )}
          {activeTab === 'departments' && (
            <button onClick={() => setIsDeptModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>
              <Plus size={18} /> Add Department
            </button>
          )}
          {activeTab === 'assignments' && (
            <button onClick={() => setIsAssignmentModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>
              <Plus size={18} /> Assign to Project
            </button>
          )}
        </div>
      </div>

      {/* KPI STRIP */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Total Employees</span><Users size={18} className="text-blue-400" /></div>
          <div className="text-3xl font-bold font-mono text-white">{staff.length}</div>
        </div>
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Active Staff</span><CheckCircle size={18} className="text-green-400" /></div>
          <div className="text-3xl font-bold font-mono text-white">{staff.filter(s => s.is_active).length}</div>
        </div>
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Departments</span><Briefcase size={18} className="text-purple-400" /></div>
          <div className="text-3xl font-bold font-mono text-white">{departments.length}</div>
        </div>
      </div>

      {/* TABS */}
      <div className="flex items-center justify-between border-b" style={{ borderColor: 'var(--border-subtle)' }}>
        <div className="flex">
          <button onClick={() => setActiveTab('directory')} className={`pb-2 px-4 font-bold border-b-2 ${activeTab === 'directory' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>Employee Directory</button>
          <button onClick={() => setActiveTab('departments')} className={`pb-2 px-4 font-bold border-b-2 ${activeTab === 'departments' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>Departments</button>
          <button onClick={() => setActiveTab('assignments')} className={`pb-2 px-4 font-bold border-b-2 ${activeTab === 'assignments' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>Project Assignments (Matrix)</button>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="flex flex-wrap items-center gap-3 p-2 rounded-lg border mb-4" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
        <span className="text-xs font-bold uppercase tracking-wider text-gray-500 pl-2 flex items-center gap-1"><Filter size={14}/> Filters</span>
        <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className="bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded px-2 py-1 text-xs text-white">
          <option value="All">All Companies</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)} className="bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded px-2 py-1 text-xs text-white">
          <option value="All">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)} className="bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded px-2 py-1 text-xs text-white">
          <option value="All">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={filterDesig} onChange={e => setFilterDesig(e.target.value)} className="bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded px-2 py-1 text-xs text-white">
          <option value="All">All Designations</option>
          {designations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      {/* TAB 1: DIRECTORY */}
      {activeTab === 'directory' && (
        <div className="rounded-xl overflow-hidden border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Employee</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Designation</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Department</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Reports To</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredStaff.map(s => (
                <tr key={s.id} onClick={() => openStaffProfile(s)} className="border-t hover:bg-[var(--bg-panel-alt)] cursor-pointer transition-colors" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="p-4 flex items-center gap-3">
                    {s.photo_url ? <img src={s.photo_url} alt={s.full_name} className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-[var(--brand-accent)] text-white flex items-center justify-center font-bold text-xs">{s.full_name.charAt(0)}</div>}
                    <div>
                      <div className="text-white font-medium">{s.full_name}</div>
                      <div className="text-xs text-gray-500 font-mono">{s.employee_code}</div>
                    </div>
                  </td>
                  <td className="p-4 text-gray-300 text-sm">{s.designation || '-'}</td>
                  <td className="p-4 text-gray-300 text-sm">{s.department_name || '-'}</td>
                  <td className="p-4 text-gray-300 text-sm">{s.reports_to_name || '-'}</td>
                  <td className="p-4 text-center"><span className={`px-2 py-1 rounded text-xs font-bold ${s.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{s.is_active ? 'Active' : 'Inactive'}</span></td>
                </tr>
              ))}
              {filteredStaff.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gray-500">No employees match your filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 2: DEPARTMENTS */}
      {activeTab === 'departments' && (
        <div className="rounded-xl overflow-hidden border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Department Name</th>
              </tr>
            </thead>
            <tbody>
              {departments.map(d => (
                <tr key={d.id} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="p-4 text-white font-medium">{d.name}</td>
                </tr>
              ))}
              {departments.length === 0 && <tr><td className="p-6 text-center text-gray-500">No departments defined yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 3: ASSIGNMENTS */}
      {activeTab === 'assignments' && (
        <div className="rounded-xl overflow-hidden border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Employee</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Project</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Project Role</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-right">Allocation</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map(a => (
                <tr key={a.id} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="p-4 text-white font-medium">{a.staff_name}</td>
                  <td className="p-4 text-[var(--brand-accent)] font-medium">{a.project_name}</td>
                  <td className="p-4 text-gray-300">{a.role}</td>
                  <td className="p-4 text-right font-mono text-gray-300">{a.allocation_pct}%</td>
                </tr>
              ))}
              {assignments.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-gray-500">No project assignments found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* HR ARCHITECTURE HELP SLIDE-OUT */}
      {isHelpOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1" onClick={() => setIsHelpOpen(false)}></div>
          <div className="w-full md:max-w-md h-full bg-[var(--bg-panel)] shadow-2xl border-l border-[var(--border-subtle)] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)]">
              <h2 className="text-xl font-bold text-white flex items-center gap-2"><HelpCircle size={20} className="text-[var(--brand-accent)]"/> Matrix Architecture Guide</h2>
              <button onClick={() => setIsHelpOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <div className="p-6 flex-1 overflow-y-auto space-y-6 text-sm text-gray-300">
              <div><h3 className="text-base font-bold text-white mb-2">1. Permanent Hierarchy (Reporting To)</h3><p>Every employee has a permanent manager they report to. This governs HR approvals and appraisals.</p></div>
              <div><h3 className="text-base font-bold text-white mb-2">2. Temporary Matrix (Project Assignments)</h3><p>Employees are assigned to specific projects for specific time periods, independent of their HR manager.</p></div>
              <div><h3 className="text-base font-bold text-white mb-2">3. Master Data (Designations & Departments)</h3><p>To ensure accurate reporting, Designations and Departments are strictly controlled. Use the "+ Create New" buttons inside the form.</p></div>
            </div>
          </div>
        </div>
      )}

            {/* ADD EMPLOYEE SLIDE-OUT */}
      {isStaffModalOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1" onClick={() => setIsStaffModalOpen(false)}></div>
          <div className="w-full md:max-w-lg h-full bg-[var(--bg-panel)] shadow-2xl border-l border-[var(--border-subtle)] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)]">
              <h2 className="text-xl font-bold text-white">Add New Employee</h2>
              <button onClick={() => setIsStaffModalOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            
            <form onSubmit={handleStaffSubmit} className="p-6 flex-1 overflow-y-auto space-y-5">
              
              {/* Row 1: Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Full Name</label>
                <input required type="text" value={staffData.full_name} onChange={e => setStaffData({...staffData, full_name: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" />
              </div>

              {/* Row 2: Email & Phone (Equal 50/50 width with larger gap) */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                  <input required type="email" value={staffData.email} onChange={e => setStaffData({...staffData, email: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Phone</label>
                  <input type="tel" value={staffData.phone} onChange={e => setStaffData({...staffData, phone: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" />
                </div>
              </div>

              {/* Row 3: DOB / Age Toggle & Date of Joining */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-300">Date of Birth / Age</label>
                    <button type="button" onClick={() => setDobType(dobType === 'dob' ? 'age' : 'dob')} className="text-xs text-[var(--brand-accent)] hover:underline">
                      Switch to {dobType === 'dob' ? 'Age' : 'DOB'}
                    </button>
                  </div>
                  {dobType === 'dob' ? (
                    <input type="date" value={staffData.date_of_birth} onChange={e => setStaffData({...staffData, date_of_birth: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" style={{ colorScheme: 'dark' }} />
                  ) : (
                    <input type="number" min="18" max="100" value={staffData.age} onChange={e => setStaffData({...staffData, age: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" placeholder="Enter Age" />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Date of Joining</label>
                  <input type="date" value={staffData.date_of_joining} onChange={e => setStaffData({...staffData, date_of_joining: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" style={{ colorScheme: 'dark' }} />
                </div>
              </div>

              
 	      {/* Row 4: Photo URL with Upload Button and Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Photo</label>
                <div className="flex items-center gap-6">
                  
                  {/* Left Side: Input and Upload Button */}
                  <div className="flex-1 flex flex-col justify-center space-y-3 py-2">
                    <input 
                      type="text" 
                      value={staffData.photo_url} 
                      onChange={e => setStaffData({...staffData, photo_url: e.target.value})} 
                      className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" 
                      placeholder="/assets/photo.jpg or https://..." 
                    />
                    
                    <div className="flex items-center gap-3">
                    {/* Hidden File Input */}
                    <input 
                      type="file" 
                      id="photo-upload" 
                      accept="image/png, image/jpeg, image/jpg, image/webp" 
                      className="hidden" 

                      // ==============================
		      // HANDLE IMAGE UPLOAD TO BACKEND
	              // ==============================
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        // Show local preview immediately
                        const localUrl = URL.createObjectURL(file);
                        setStaffData(prev => ({...prev, photo_url: localUrl}));

                        // Upload to server
                        const formData = new FormData();
                        formData.append('image', file);

                        try {
                          const res = await fetch('/api/hr/upload', { method: 'POST', body: formData });
                          if (!res.ok) throw new Error('Upload failed');
                          const data = await res.json();
                          
                          // Replace local preview with the real server URL
                          setStaffData(prev => ({...prev, photo_url: data.url}));
                        } catch (err) {
                          console.error("Upload error:", err);
                          alert("Failed to upload image.");
                        }
                      }} 
                    />  
                    
                      {/* Upload Button with Icon */}
                      <label 
                        htmlFor="photo-upload" 
                        className="flex items-center gap-2 px-4 py-2 rounded text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-opacity" 
                        style={{ backgroundColor: 'var(--brand-accent)' }}
                      >
                        <UserPlus size={16} /> Upload Image
                      </label>
                      <span className="text-xs text-gray-500">(JPG, PNG, WEBP)</span>
                    </div>
                  </div>

                  {/* Right Side: Large Preview Box */}
                  <div className="w-24 h-32 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-panel-alt)] flex flex-col items-center justify-center overflow-hidden shrink-0 relative group">
                    {staffData.photo_url ? (
                      <>
                        <img src={staffData.photo_url} alt="Preview" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = 'none')} />
                        <button 
                          type="button" 
                          onClick={() => setStaffData({...staffData, photo_url: ''})} 
                          className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove Image"
                        >
                          <X size={12} />
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-gray-500 text-center leading-tight">No<br/>Img</span>
                    )}
                  </div>
                  
                </div>
              </div>

              {/* Row 5: Company & Placement */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Company</label>
                  <select value={staffData.company_id} onChange={e => setStaffData({...staffData, company_id: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white">
                    <option value="">Select Company</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Placement / Location</label>
                  <input type="text" value={staffData.placement_location} onChange={e => setStaffData({...staffData, placement_location: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" placeholder="e.g. Head Office" />
                </div>
              </div>

              {/* Row 6: Designation & Department */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1 flex justify-between items-center">
                    <span>Designation</span>
                    {!isAddingInlineDesig && <button type="button" onClick={() => setIsAddingInlineDesig(true)} className="text-xs text-[var(--brand-accent)] hover:underline">+ New</button>}
                  </label>
                  {isAddingInlineDesig ? (
                    <div className="flex gap-2">
                      <input required type="text" value={newDesigName} onChange={e => setNewDesigName(e.target.value)} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" placeholder="New role" />
                      <button type="button" onClick={async (e) => {
                        e.preventDefault();
                        if (!newDesigName) return;
                        const res = await fetch('/api/hr/designations', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: newDesigName }) });
                        const data = await res.json();
                        setNewDesigName(''); setIsAddingInlineDesig(false); await fetchData();
                        if (data && data.id) setStaffData({...staffData, designation_id: data.id});
                      }} className="px-3 py-2 rounded text-white font-bold" style={{ backgroundColor: 'var(--brand-accent)' }}>Save</button>
                    </div>
                  ) : (
                    <select value={staffData.designation_id} onChange={e => setStaffData({...staffData, designation_id: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white">
                      <option value="">Select Designation</option>
                      {designations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1 flex justify-between items-center">
                    <span>Department</span>
                    {!isAddingInlineDept && <button type="button" onClick={() => setIsAddingInlineDept(true)} className="text-xs text-[var(--brand-accent)] hover:underline">+ New</button>}
                  </label>
                  {isAddingInlineDept ? (
                    <div className="flex gap-2">
                      <input required type="text" value={newDeptName} onChange={e => setNewDeptName(e.target.value)} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" placeholder="New dept" />
                      <button type="button" onClick={async (e) => {
                        e.preventDefault();
                        if (!newDeptName) return;
                        const res = await fetch('/api/hr/departments', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: newDeptName, company_id: companies[0]?.id }) });
                        const data = await res.json();
                        setNewDeptName(''); setIsAddingInlineDept(false); await fetchData();
                        if (data && data.id) setStaffData({...staffData, department_id: data.id});
                      }} className="px-3 py-2 rounded text-white font-bold" style={{ backgroundColor: 'var(--brand-accent)' }}>Save</button>
                    </div>
                  ) : (
                    <select value={staffData.department_id} onChange={e => setStaffData({...staffData, department_id: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white">
                      <option value="">Select Department</option>
                      {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  )}
                </div>
              </div>

              {/* Row 7: Reports To */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Reports To</label>
                <select value={staffData.reports_to_staff_id} onChange={e => setStaffData({...staffData, reports_to_staff_id: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white">
                  <option value="">No Manager</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                </select>
              </div>

              {/* Row 8: Contact Address */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Contact Address</label>
                <textarea rows={6} value={staffData.contact_address} onChange={e => setStaffData({...staffData, contact_address: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" placeholder="Enter full address..." />
              </div>

              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-6" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Employee</button>
            </form>
          </div>
        </div>
      )}

      {/* ADD DEPARTMENT MODAL */}
      {isDeptModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1 absolute inset-0" onClick={() => setIsDeptModalOpen(false)}></div>
          <div className="w-full max-w-md bg-[var(--bg-panel)] shadow-2xl border border-[var(--border-subtle)] rounded-xl flex flex-col relative z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)] rounded-t-xl"><h2 className="text-xl font-bold text-white">Add Department</h2><button onClick={() => setIsDeptModalOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button></div>
            <form onSubmit={handleDeptSubmit} className="p-6 space-y-4"><div><label className="block text-sm font-medium text-gray-300 mb-1">Department Name</label><input required type="text" value={newDeptName} onChange={e => setNewDeptName(e.target.value)} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" /></div><button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Department</button></form>
          </div>
        </div>
      )}

      {/* ADD ASSIGNMENT MODAL */}
      {isAssignmentModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1 absolute inset-0" onClick={() => setIsAssignmentModalOpen(false)}></div>
          <div className="w-full max-w-md bg-[var(--bg-panel)] shadow-2xl border border-[var(--border-subtle)] rounded-xl flex flex-col relative z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)] rounded-t-xl"><h2 className="text-xl font-bold text-white">Assign to Project</h2><button onClick={() => setIsAssignmentModalOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button></div>
            <form onSubmit={handleAssignmentSubmit} className="p-6 space-y-4">
              <div><label className="block text-sm font-medium text-gray-300 mb-1">Employee</label><select required value={assignmentData.staff_id} onChange={e => setAssignmentData({...assignmentData, staff_id: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white"><option value="">Select Employee</option>{staff.map(s => <option key={s.id} value={s.id}>{s.full_name} ({s.designation})</option>)}</select></div>
              <div><label className="block text-sm font-medium text-gray-300 mb-1">Project</label><select required value={assignmentData.project_id} onChange={e => setAssignmentData({...assignmentData, project_id: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white"><option value="">Select Project</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
              <div><label className="block text-sm font-medium text-gray-300 mb-1">Project Role</label><input required type="text" value={assignmentData.role} onChange={e => setAssignmentData({...assignmentData, role: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" /></div>
              <div><label className="block text-sm font-medium text-gray-300 mb-1">Allocation %</label><input required type="number" min="1" max="100" value={assignmentData.allocation_pct} onChange={e => setAssignmentData({...assignmentData, allocation_pct: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" /></div>
              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Assignment</button>
            </form>
          </div>
        </div>
      )}

      {/* STAFF DETAIL SLIDE-OUT (TABBED) */}
      {selectedStaff && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1" onClick={() => setSelectedStaff(null)}></div>
          <div className="w-full md:max-w-xl h-full bg-[var(--bg-panel)] shadow-2xl border-l border-[var(--border-subtle)] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-[var(--border-subtle)] bg-[var(--bg-panel-alt)] flex justify-between items-start">
              <div className="flex items-center gap-4">
                {selectedStaff.photo_url ? <img src={selectedStaff.photo_url} alt={selectedStaff.full_name} className="w-16 h-16 rounded-full object-cover border-2 border-[var(--border-subtle)]" /> : <div className="w-16 h-16 rounded-full bg-[var(--brand-accent)] text-white flex items-center justify-center font-bold text-2xl border-2 border-[var(--border-subtle)]">{selectedStaff.full_name.charAt(0)}</div>}
                <div><h2 className="text-2xl font-bold text-white">{selectedStaff.full_name}</h2><p className="text-sm text-[var(--brand-accent)] font-medium">{selectedStaff.designation || 'No Designation'}</p><p className="text-xs text-gray-400 font-mono mt-1">{selectedStaff.employee_code}</p></div>
              </div>
              <button onClick={() => setSelectedStaff(null)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <div className="flex border-b border-[var(--border-subtle)] bg-[var(--bg-panel)]">
              <button onClick={() => setActiveProfileTab('profile')} className={`flex-1 py-3 text-sm font-bold border-b-2 ${activeProfileTab === 'profile' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>HR Profile</button>
              <button onClick={() => setActiveProfileTab('salary')} className={`flex-1 py-3 text-sm font-bold border-b-2 ${activeProfileTab === 'salary' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>Salary & Revisions</button>
              <button onClick={() => setActiveProfileTab('kpi')} className={`flex-1 py-3 text-sm font-bold border-b-2 ${activeProfileTab === 'kpi' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>KPIs & Appraisals</button>
            </div>
            <div className="p-6 flex-1 overflow-y-auto space-y-6">
              {activeProfileTab === 'profile' && (
                <>
                  <div><h3 className="text-sm font-bold text-gray-300 mb-3 flex items-center gap-2"><Briefcase size={16} /> Work Information</h3><div className="space-y-3 bg-[var(--bg-panel-alt)] p-4 rounded-xl border border-[var(--border-subtle)]"><div className="flex justify-between text-sm"><span className="text-gray-400">Department</span><span className="text-white font-medium">{selectedStaff.department_name || '-'}</span></div><div className="flex justify-between text-sm"><span className="text-gray-400">Reports To</span><span className="text-white font-medium">{selectedStaff.reports_to_name || 'No Manager'}</span></div><div className="flex justify-between text-sm"><span className="text-gray-400">Date of Joining</span><span className="text-white font-mono">{selectedStaff.date_of_joining ? new Date(selectedStaff.date_of_joining).toLocaleDateString() : '-'}</span></div></div></div>
                  <div><h3 className="text-sm font-bold text-gray-300 mb-3 flex items-center gap-2"><FileText size={16} /> Contact Details</h3><div className="space-y-3 bg-[var(--bg-panel-alt)] p-4 rounded-xl border border-[var(--border-subtle)]"><div className="flex justify-between text-sm"><span className="text-gray-400">Email</span><span className="text-white font-medium">{selectedStaff.email || '-'}</span></div><div className="flex justify-between text-sm"><span className="text-gray-400">Phone</span><span className="text-white font-medium">{selectedStaff.phone || '-'}</span></div></div></div>
                </>
              )}
              {activeProfileTab === 'salary' && (
                <>
                  {isSalaryLoading ? <div className="text-center text-gray-500 py-10">Loading salary data...</div> : (
                    <>
                      <div><div className="flex justify-between items-center mb-3"><h3 className="text-sm font-bold text-gray-300 flex items-center gap-2">Current Salary Structure</h3><button onClick={() => setIsSalaryFormOpen(true)} className="text-xs font-bold text-[var(--brand-accent)] hover:underline">Edit Structure</button></div><div className="space-y-2 bg-[var(--bg-panel-alt)] p-4 rounded-xl border border-[var(--border-subtle)]"><div className="flex justify-between text-sm"><span className="text-gray-400">Basic Salary</span><span className="text-white font-mono">₹{Number(salaryData?.basic_salary || 0).toLocaleString('en-IN')}</span></div><div className="flex justify-between text-sm"><span className="text-gray-400">HRA</span><span className="text-white font-mono">₹{Number(salaryData?.hra || 0).toLocaleString('en-IN')}</span></div><div className="flex justify-between text-sm"><span className="text-gray-400">Special Allowance</span><span className="text-white font-mono">₹{Number(salaryData?.special_allowance || 0).toLocaleString('en-IN')}</span></div><div className="flex justify-between text-sm text-red-400"><span>PF Deduction</span><span className="font-mono">-₹{Number(salaryData?.pf_deduction || 0).toLocaleString('en-IN')}</span></div><div className="flex justify-between text-sm text-red-400"><span>Tax Deduction</span><span className="font-mono">-₹{Number(salaryData?.tax_deduction || 0).toLocaleString('en-IN')}</span></div><div className="flex justify-between text-base pt-2 border-t border-[var(--border-subtle)] mt-2 font-bold"><span className="text-white">Net Salary</span><span className="text-green-400 font-mono">₹{Number(salaryData?.net_salary || 0).toLocaleString('en-IN')}</span></div></div></div>
                      <div className="mt-6"><div className="flex justify-between items-center mb-3"><h3 className="text-sm font-bold text-gray-300 flex items-center gap-2">Revision History</h3><button onClick={() => setIsRevisionFormOpen(true)} className="text-xs font-bold text-[var(--brand-accent)] hover:underline">+ Add Revision</button></div>
                        {revisions.length === 0 ? <div className="text-sm text-gray-500 italic p-3 text-center border border-[var(--border-subtle)] rounded bg-[var(--bg-panel-alt)]">No salary revisions yet.</div> : <div className="space-y-2">{revisions.map(r => <div key={r.id} className="p-3 rounded border border-[var(--border-subtle)] bg-[var(--bg-panel-alt)]"><div className="flex justify-between items-center mb-1"><span className="text-green-400 font-mono font-bold">+₹{Number(r.revision_amount).toLocaleString('en-IN')}</span><span className="text-xs text-gray-400">{new Date(r.effective_date).toLocaleDateString()}</span></div><p className="text-sm text-white font-medium">{r.reason}</p><p className="text-xs text-gray-400 mt-1">New Net Salary: ₹{Number(r.new_net_salary).toLocaleString('en-IN')}</p></div>)}</div>}
                      </div>
                    </>
                  )}
                </>
              )}
              {activeProfileTab === 'kpi' && (
                <div><div className="flex justify-between items-center mb-3"><h3 className="text-sm font-bold text-gray-300 flex items-center gap-2">Appraisal History</h3><button onClick={() => setIsKpiFormOpen(true)} className="text-xs font-bold text-[var(--brand-accent)] hover:underline">+ Add Appraisal</button></div>
                  {kpiData.length === 0 ? <div className="text-sm text-gray-500 italic p-3 text-center border border-[var(--border-subtle)] rounded bg-[var(--bg-panel-alt)]">No KPIs recorded yet.</div> : <div className="space-y-3">{kpiData.map(k => <div key={k.id} className="p-4 rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-panel-alt)]"><div className="flex justify-between items-center mb-2"><span className="text-white font-bold">{k.review_period}</span><span className={`px-2 py-1 rounded text-xs font-bold ${k.kpi_score >= 80 ? 'bg-green-500/20 text-green-400' : k.kpi_score >= 50 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>Score: {k.kpi_score}/100</span></div><p className="text-sm text-gray-300 italic">"{k.reviewer_comments}"</p><p className="text-xs text-gray-500 mt-2">Date: {new Date(k.appraisal_date).toLocaleDateString()}</p></div>)}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
